// src/pages/super-admin/Screens/DashboardPage.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Info,
  Users,
  CreditCard,
  CheckCircle2,
  AlertTriangle,
  CalendarDays,
  Megaphone,
  Send,
  Settings as Cog,
} from "lucide-react";

const PRIMARY = "#1F3A93";


/** Force-visible quiet style for outline buttons on white/glass rows */
const QUIET =
  "!bg-white !text-gray-900 !border-black/10 border ring-1 ring-black/5 shadow-sm rounded-xl hover:!bg-gray-50 active:!bg-gray-50";


/* -------- Mock data (replace with API later) -------- */
const mock = {
  adminName: "",
  payments: {
    pending: 3,
    forwardedToday: 5,
    lastIncomingAt: "2025-08-15T09:32:00",
  },
  approvals: [
    { id: "a-201", condo: "Residencias Arce", contact: "james@maplehoa.com", submittedAt: "2025-08-13" },
    { id: "a-202", condo: "Plaza Jardin", contact: "apply@garden.com", submittedAt: "2025-08-14" },
  ],
  payouts: [
    { id: "o-501", hoaAdmin: "Sofia Mendes", condo: "Villas Atardecer", amount: 95, paidAt: "2025-08-12" },
    { id: "o-502", hoaAdmin: "Maria Lopez", condo: "Torre Azul HOA", amount: 480, paidAt: "2025-08-01" },
  ],
  announcement: {
    titleKey: "superAdmin.dashboard.platformNominal",
    whenDaysAgo: 1,
    tagKey: "common.info",
  },
};

export default function SADashboardPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const locale = "es-ES";
  const formatDate = React.useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
    [locale],
  );
  const formatDateTime = React.useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    [locale],
  );
  const formatCurrency = React.useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "USD",
      }),
    [locale],
  );
  const adminName = mock.adminName || t("roles.superAdmin");
    
  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Greeting / status */}
      <Card variant="glass" className="rounded-3xl p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="shrink-0 rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
            <Info className="w-5 h-5 text-gray-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-[18px] sm:text-[20px] font-semibold tracking-[-0.01em] text-gray-900">
              {t("superAdmin.dashboard.greeting", { name: adminName })}
            </h2>
            <p className="text-gray-600 mt-1 text-sm">{t("superAdmin.dashboard.platformConsole")} • {t("superAdmin.dashboard.platformNominal")}</p>
          </div>
        </div>
      </Card>

      {/* Payments Inbox status (amber/green rail based on pending) */}
      <Card
        variant="glass"
        rail
        railColor={mock.payments.pending > 0 ? "border-amber-500" : "border-emerald-500"}
        railWidth="border-l-4"
        className="rounded-3xl p-5 sm:p-6"
      >
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
          <h3 className="font-semibold text-lg tracking-tight text-gray-900">{t("superAdmin.dashboard.paymentsInbox.title")}</h3>
          <div className="inline-flex items-center gap-2 text-sm font-medium">
            {mock.payments.pending > 0 ? (
              <>
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="text-amber-700">{t("superAdmin.dashboard.paymentsInbox.pending", { count: mock.payments.pending })}</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span className="text-emerald-700">{t("superAdmin.dashboard.paymentsInbox.noPending")}</span>
              </>
            )}
          </div>
        </div>

        <div className="mt-3 grid sm:grid-cols-3 gap-3">
          <div className="rounded-2xl px-4 py-3 text-sm bg-gray-50 ring-1 ring-black/5">
            <div className="font-medium text-gray-900">{t("superAdmin.dashboard.paymentsInbox.forwardedToday")}</div>
            <div className="text-gray-700/80">{mock.payments.forwardedToday}</div>
          </div>
          <div className="rounded-2xl px-4 py-3 text-sm bg-gray-50 ring-1 ring-black/5">
            <div className="font-medium text-gray-900">{t("superAdmin.dashboard.paymentsInbox.lastIncoming")}</div>
            <div className="text-gray-700/80">{formatDateTime.format(new Date(mock.payments.lastIncomingAt))}</div>
          </div>
          <div className="rounded-2xl px-4 py-3 text-sm bg-gray-50 ring-1 ring-black/5">
            <div className="font-medium text-gray-900">{t("superAdmin.dashboard.paymentsInbox.quickJump")}</div>
            <Button
              onClick={() => navigate("/super-admin/payments")}
              className="mt-2 rounded-2xl px-3 py-2 h-auto text-white"
              style={{ backgroundColor: PRIMARY }}
            >
              {t("superAdmin.dashboard.paymentsInbox.openInbox")}
            </Button>
          </div>
        </div>
      </Card>

      {/* Quick actions (consistent tiles) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <ActionTile
          title={t("superAdmin.dashboard.actions.manage")}
          subtitle={t("superAdmin.dashboard.actions.hoaAdmins")}
          icon={<Users className="w-6 h-6" />}
          onClick={() => navigate("/super-admin/hoa-admins")}
        />
        <ActionTile
          title={t("superAdmin.dashboard.actions.view")}
          subtitle={t("superAdmin.dashboard.actions.payouts")}
          icon={<Send className="w-6 h-6" />}
          onClick={() => navigate("/super-admin/payments")}
        />
        <ActionTile
          title={t("superAdmin.dashboard.actions.open")}
          subtitle={t("superAdmin.dashboard.actions.paymentsInbox")}
          icon={<CreditCard className="w-6 h-6" />}
          onClick={() => navigate("/super-admin/payments")}
        />
        <ActionTile
          title={t("superAdmin.dashboard.actions.platform")}
          subtitle={t("superAdmin.dashboard.actions.settings")}
          icon={<Cog className="w-6 h-6" />}
          onClick={() => navigate("/super-admin/settings")}
        />
      </div>

      {/* Pending HOA‑Admin approvals */}
      <Card
        variant="glass"
        rail
        railColor="border-blue-400"
        railWidth="border-l-4"
        className="rounded-3xl p-5 sm:p-6"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
            <Users className="w-5 h-5 text-gray-700" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-lg tracking-tight text-gray-900">
                  {t("superAdmin.dashboard.approvals.title")}
                </h3>
                <Badge className="bg-white text-black ring-1 ring-black/10">
                  {t("superAdmin.dashboard.approvals.pending", { count: mock.approvals.length })}
                </Badge>
              </div>
              <div className="sm:ml-auto inline-flex items-center gap-1 text-sm text-gray-600">
                <CalendarDays className="w-4 h-4" />
                {t("superAdmin.dashboard.approvals.updatedNow")}
              </div>
            </div>

            <ul className="mt-3 space-y-2">
              {mock.approvals.map((a) => (
                <li
                  key={a.id}
                  className="rounded-xl bg-white/80 backdrop-blur ring-1 ring-black/5 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900">{a.condo}</div>
                    <div className="text-sm text-gray-600">{a.contact}</div>
                  </div>
                  <div className="sm:ml-auto text-sm text-gray-600 flex items-center gap-2">
                    <CalendarDays className="w-4 h-4" />
                    {formatDate.format(new Date(a.submittedAt))}
                  </div>
                  <div className="flex gap-2 sm:ml-2">
                    <Button
                      size="sm"
                      className="rounded-xl h-8 px-3 text-white"
                      style={{ backgroundColor: PRIMARY }}
                      onClick={() => {/* approve flow */}}
                    >
                      {t("superAdmin.dashboard.approvals.approve")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className={`rounded-xl h-8 px-3 ${QUIET}`}
                      onClick={() => {/* reject flow */}}
                    >
                      {t("superAdmin.dashboard.approvals.reject")}
                    </Button>
                  </div>
                </li>
              ))}
              {mock.approvals.length === 0 && (
                <li className="text-sm text-gray-600">{t("superAdmin.dashboard.approvals.noPending")}</li>
              )}
            </ul>
          </div>
        </div>
      </Card>

      {/* Recent payouts */}
      <Card
        variant="glass"
        rail
        railColor="border-emerald-500"
        railWidth="border-l-4"
        className="rounded-3xl p-5 sm:p-6"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
            <Send className="w-5 h-5 text-gray-700" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-lg tracking-tight text-gray-900">
                  {t("superAdmin.dashboard.payouts.title")}
                </h3>
                <Badge className="bg-white text-black ring-1 ring-black/10">{t("superAdmin.dashboard.payouts.last14Days")}</Badge>
              </div>
              <div className="sm:ml-auto inline-flex items-center gap-1 text-sm text-gray-600">
                <CalendarDays className="w-4 h-4" />
                {t("superAdmin.dashboard.payouts.records", { count: mock.payouts.length })}
              </div>
            </div>

            <ul className="mt-3 space-y-2">
              {mock.payouts.map((p) => (
                <li
                  key={p.id}
                  className="rounded-xl bg-white/80 backdrop-blur ring-1 ring-black/5 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900">{p.hoaAdmin}</div>
                    <div className="text-sm text-gray-600">{p.condo}</div>
                  </div>
                  <div className="sm:ml-auto text-sm text-gray-700 flex items-center gap-3">
                    <span className="font-medium text-gray-900">{formatCurrency.format(p.amount)}</span>
                    <span className="text-gray-500">•</span>
                    <span>{formatDate.format(new Date(p.paidAt))}</span>
                  </div>
                </li>
              ))}
              {mock.payouts.length === 0 && (
                <li className="text-sm text-gray-600">{t("superAdmin.dashboard.payouts.noPayouts")}</li>
              )}
            </ul>

            <div className="mt-4 flex justify-end">
              <Button
                variant="outline"
                className={`rounded-2xl px-4 py-2.5 ${QUIET}`}
                onClick={() => navigate("/super-admin/payments")}
              >
                {t("superAdmin.dashboard.payouts.viewAll")}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Platform reminder (same “Latest announcement” pattern) */}
      {/* <Card
        variant="glass"
        rail
        railColor="border-amber-500"
        railWidth="border-l-4"
        className="rounded-3xl p-5 sm:p-6"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
            <Megaphone className="w-5 h-5 text-gray-700" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-lg tracking-tight text-gray-900">
                  {t("superAdmin.dashboard.systemNotes.title")}
                </h3>
                <Badge className="bg-white text-black ring-1 ring-black/10">
                  {t(mock.announcement.tagKey)}
                </Badge>
              </div>
              <div className="sm:ml-auto inline-flex items-center gap-1 text-sm text-gray-600">
                <CalendarDays className="w-4 h-4" />
                {t("date.daysAgo", { count: mock.announcement.whenDaysAgo })}
              </div>
            </div>

            <p className="text-gray-800 mt-2 leading-6 break-words">
              {t(mock.announcement.titleKey)}
            </p>
          </div>
        </div>
      </Card> */}
    </div>
  );
}

/* ---------- Reusable tile (same style as resident) ---------- */
function ActionTile({
  title,
  subtitle,
  icon,
  onClick,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={`${title} ${subtitle}`}
      className={[
        "rounded-2xl text-left transition will-change-transform",
        "bg-white/80 backdrop-blur-xl ring-1 ring-black/5 shadow-lg shadow-black/10 hover:shadow-xl",
        "active:scale-[0.99] px-4 py-4 flex items-center gap-3",
      ].join(" ")}
    >
      <div className="shrink-0 rounded-2xl p-2 bg-white ring-1 ring-black/10 shadow-sm">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-gray-900">{title}</div>
        <div className="text-sm text-gray-600">{subtitle}</div>
      </div>
    </button>
  );
}
